import * as CL from '@design-system/component-library';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { deleteEmployeeProductSelection } from '../../common/fetch.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  closeMsg,
  descriptionMsg,
  lastModifiedMsg,
  productTypeMsg,
  somethingWentWrongMsg,
  t,
} from '../../common/i18n/index.js';
import { formatDateStringToDDMMYYYYHHmm } from '../../common/utils/dateUtilsCoCo.js';
import {
  deviceSelectionTabPath,
  getDeviceSelectionProductTypeLabel,
  getEditDeviceSelectionPath,
} from '../../common/utils/deviceListTabsUtilsCoCo.js';
import { EMPTY_VALUE } from '../../common/utils/deviceListUtilsCoCo.js';
import { toDeviceSelectionProductType } from '../../common/utils/deviceSelectionListUtilsCoCo.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { ConfirmationDialog } from '../ConfirmationDialog/index.js';
import { DialogWrapper } from '../DialogWrapper/index.js';
import { DeviceSelectionDevicesCoCo } from './DeviceSelectionDevicesCoCo.js';
import { DeviceSelectionUsedInDeviceListsCoCo } from './DeviceSelectionUsedInDeviceListsCoCo.js';
import type { DeviceVariantGroupsByTypeCoCo } from '../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import type { EmployeeProductSelectionResponse } from '../../generated/api/employeeProductSelectionResponse.js';

import './DeviceSelectionDetailsCoCo.scss';

interface DeviceSelectionDetailsCoCoProps {
  deviceSelection: EmployeeProductSelectionResponse;
  deviceVariantGroups: DeviceVariantGroupsByTypeCoCo;
}

export const DeviceSelectionDetailsCoCo = ({
  deviceSelection,
  deviceVariantGroups,
}: DeviceSelectionDetailsCoCoProps) => {
  const navigate = useNavigate();
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const accountMdmId = mdmId ?? deviceSelection.accountMdmId;
  const [dialog, setDialog] = useState<'in-use' | 'confirm'>();
  const [deleting, setDeleting] = useState(false);
  const deletingRef = useRef(false);
  const closeDialog = () => {
    if (!deletingRef.current) {
      setDialog(undefined);
    }
  };
  const handleDelete = async () => {
    if (deletingRef.current) {
      return;
    }
    deletingRef.current = true;
    setDeleting(true);
    try {
      const response = await deleteEmployeeProductSelection(deviceSelection.uuid, accountMdmId);
      if (!response.ok) {
        throw new Error('Failed to delete device selection');
      }
      setDialog(undefined);
      navigate(`${deviceSelectionTabPath}&${new URLSearchParams({ mdmId: accountMdmId })}`);
    } catch {
      addNotification(t.JDTO(somethingWentWrongMsg), 'error');
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };
  return (
    <div className="of-device-selection-details-coco__content">
      <CL.Description
        columns={1}
        items={[{ title: t.HBW3(descriptionMsg), description: deviceSelection.description || EMPTY_VALUE }]}
      />
      <CL.Description
        items={[
          {
            title: t.R6HP(productTypeMsg),
            description: getDeviceSelectionProductTypeLabel(toDeviceSelectionProductType(deviceSelection.productType)),
          },
          {
            title: t.PM1H(lastModifiedMsg),
            description: formatDateStringToDDMMYYYYHHmm(deviceSelection.lastModifiedDate) ?? EMPTY_VALUE,
          },
        ]}
      />
      <div className="of-device-selection-details-coco__actions">
        <CL.Button
          color="link"
          size="l"
          onClick={() => setDialog(deviceSelection.usedInProductLists?.length ? 'in-use' : 'confirm')}
        >
          <CL.Icon icon="delete" />
          {t.YBM6('Remove device selection')}
        </CL.Button>
        <CL.Button
          color="link"
          size="l"
          onClick={() => navigate(getEditDeviceSelectionPath(deviceSelection.uuid, accountMdmId))}
        >
          <CL.Icon icon="edit" />
          {t.P81H('Edit device selection')}
        </CL.Button>
      </div>
      <DeviceSelectionUsedInDeviceListsCoCo deviceLists={deviceSelection.usedInProductLists} />
      <DeviceSelectionDevicesCoCo deviceSelection={deviceSelection} deviceVariantGroups={deviceVariantGroups} />
      {dialog === 'in-use' && (
        <DialogWrapper
          closeable
          header={t.UXAH('Device selection in use')}
          onCloseDialog={closeDialog}
          buttons={[{ text: t.WOYD(closeMsg), onClick: closeDialog }]}
        >
          {t.FJ23(
            'You cannot delete the device selection {0} because it is currently used by Device lists. Switch {0} to another Device Selection before deleting it.',
            deviceSelection.name
          )}
        </DialogWrapper>
      )}
      {dialog === 'confirm' && (
        <ConfirmationDialog
          header={t.YBM6('Remove device selection')}
          body={t.MUEL('Are you sure you want to delete the device selection {0}?', deviceSelection.name)}
          confirmButtonText={t.YBM6('Remove device selection')}
          onCloseDialog={closeDialog}
          onConfirm={handleDelete}
          disableConfirm={deleting}
          isLoading={deleting}
          hideClosingCross={deleting}
        />
      )}
    </div>
  );
};
